import { Reason } from 'proto/generated/tintin_content_policy';
import { EntityType } from 'proto/generated/inspector_gadget';

export enum Field {
  REPORTER_EMAIL = 'reporterEmail',
  REPORTED_CONTENT_URIS = 'productUris',
  REPORTED_ASSET_ID = 'assetId',
  REPORTED_ENTITY_TYPE = 'entityType',
  REPORTED_CONTENT_TYPE = 'contentType',
  COMMON_ASSET_TYPE = 'commonAssetType',
  REPORTER_COUNTRY_CODE = 'reporterCountryCode',
  VERIFICATION_RESOURCE_URI = 'resourceUri',
  VERIFICATION_CAPTCHA_TOKEN = 'captchaToken',
  VERIFICATION_CODE = 'code',
  TERM_I_BELIEVE_IS_ACCURATE = 'termIBelieveIsAccurate',
  TERM_I_UNDERSTAND_THAT_ABUSING_MAY_RESTRICT = 'termIUnderstandThatAbusingMayRestrict',
  // Extra fields for employees
  TEAM = 'team',
  TEAM_OTHER = 'teamOther',
  URGENCY = 'urgency',
  ADDITIONAL_INFO = 'additionalInfo',
  REASON = 'reason',
  USERNAME = 'username',
}

export enum OptionsUrgency {
  URGENCY_INVALID = 'URGENCY_INVALID',
  URGENCY_HIGH = 'URGENCY_HIGH',
  URGENCY_MEDIUM = 'URGENCY_MEDIUM',
  URGENCY_LOW = 'URGENCY_LOW',
}

export enum OptionsTeam {
  CREATOR_SUPPORT = 'creator_support',
  CUSTOMER_SERVICE = 'customer_service',
  PR_COMMS = 'pr_comms',
  TRUST_AND_SAFETY = 'trust_and_safety',
  ALM = 'alm',
  LEGAL = 'legal',
  CONTENT_PROTECTION = 'content_protection',
  OTHER = 'other',
}

interface FieldProperty {
  label: string;
  translationPrefix?: string;
}

export const FieldProperties: Record<Field, FieldProperty> = {
  [Field.REPORTER_EMAIL]: {
    label: 'reporter_email_label',
  },
  [Field.REPORTED_CONTENT_URIS]: {
    label: 'content_uri_label',
  },
  [Field.REPORTED_ASSET_ID]: {
    label: 'content_asset_id_label',
  },
  [Field.REPORTED_CONTENT_TYPE]: {
    label: 'content_type_label',
    translationPrefix: 'content_type',
  },
  [Field.COMMON_ASSET_TYPE]: {
    label: 'content_type_label',
    translationPrefix: 'content_type',
  },
  [Field.REPORTER_COUNTRY_CODE]: {
    label: '',
  },
  [Field.VERIFICATION_RESOURCE_URI]: {
    label: 'resource_uri',
  },
  [Field.VERIFICATION_CODE]: {
    label: 'code',
  },
  [Field.VERIFICATION_CAPTCHA_TOKEN]: {
    label: 'token',
  },
  [Field.TEAM]: {
    label: 'team_label',
    translationPrefix: 'team',
  },
  [Field.TEAM_OTHER]: {
    label: 'team_other_label',
  },
  [Field.URGENCY]: {
    label: 'urgency_label',
  },
  [Field.ADDITIONAL_INFO]: {
    label: 'additional_info_label',
  },
  [Field.REASON]: {
    label: 'report_reason_label',
    translationPrefix: 'report_reason',
  },
  [Field.TERM_I_BELIEVE_IS_ACCURATE]: {
    label: 'i_have_good_faith_believe_that_this_report_is_accurate',
  },
  [Field.TERM_I_UNDERSTAND_THAT_ABUSING_MAY_RESTRICT]: {
    label: 'i_understand_that_abusing_the_forms_may_restrict_my_ability',
  },
  [Field.USERNAME]: {
    label: 'username',
  },
  [Field.REPORTED_ENTITY_TYPE]: {
    label: '', // This field is not used on the UI itself
  },
};

export interface ReasonOption {
  value: Reason;
  label: string;
  description: string;
}

export const ReasonOptions: Array<ReasonOption> = [
  {
    value: Reason.REASON_SEXUAL,
    label: 'content_reporting_intro_page_reason_sexual_content',
    description: 'content_reporting_intro_page_reason_sexual_content_description',
  },
  {
    value: Reason.REASON_VIOLENT_OR_DANGEROUS,
    label: 'content_reporting_intro_page_reason_violent_or_dangerous',
    description: 'content_reporting_intro_page_reason_violent_or_dangerous_description',
  },
  {
    value: Reason.REASON_HATEFUL_OR_ABUSIVE,
    label: 'content_reporting_intro_page_reason_hateful_or_abusive',
    description: 'content_reporting_intro_page_reason_hateful_or_abusive_description',
  },
  {
    value: Reason.REASON_DECEPTIVE,
    label: 'content_reporting_intro_page_reason_deceptive_content',
    description: 'content_reporting_intro_page_reason_deceptive_content_description',
  },
  {
    value: Reason.REASON_CSEA,
    label: 'content_reporting_intro_page_reason_csea',
    description: 'content_reporting_intro_page_reason_csea_description',
  },
  {
    value: Reason.REASON_DO_NOT_LIKE_IT,
    label: 'content_reporting_intro_page_reason_do_not_like',
    description: 'content_reporting_intro_page_reason_do_not_like_description',
  },
];

export const UrgencyOptions: Array<OptionsUrgency> = [
  OptionsUrgency.URGENCY_HIGH,
  OptionsUrgency.URGENCY_MEDIUM,
  OptionsUrgency.URGENCY_LOW,
];

export const TeamOptions: Array<OptionsTeam> = [
  OptionsTeam.CREATOR_SUPPORT,
  OptionsTeam.CUSTOMER_SERVICE,
  OptionsTeam.PR_COMMS,
  OptionsTeam.TRUST_AND_SAFETY,
  OptionsTeam.ALM,
  OptionsTeam.LEGAL,
  OptionsTeam.CONTENT_PROTECTION,
  OptionsTeam.OTHER,
];

// the values should be consistent with what is expected in tintin:
// https://ghe.spotify.net/content-toolbox/tintin/blob/master/src/main/proto/tintin_content_policy.proto
export const ALL_COMMON_FIELDS_IN_PAYLOAD: Array<Field> = [
  Field.REPORTER_EMAIL,
  Field.REPORTED_CONTENT_URIS, // this should be an array of strings called productUris
  Field.REPORTED_CONTENT_TYPE,
  Field.COMMON_ASSET_TYPE,
  Field.REASON,
];

export const ALL_AUTHENTICATION_FIELDS_IN_PAYLOAD: Array<Field> = [
  Field.VERIFICATION_RESOURCE_URI,
  Field.VERIFICATION_CODE,
];

export const ALL_SPOTIFY_AUTHENTICATED_FIELDS_IN_PAYLOAD: Array<Field> = [Field.USERNAME];

export const ALL_FIELDS_IN_EXTERNAL_PAYLOAD: Array<Field> = [
  ...ALL_COMMON_FIELDS_IN_PAYLOAD,
  Field.TERM_I_BELIEVE_IS_ACCURATE,
  Field.TERM_I_UNDERSTAND_THAT_ABUSING_MAY_RESTRICT,
  Field.VERIFICATION_CAPTCHA_TOKEN,
];

export const ALL_FIELDS_IN_EMPLOYEE_PAYLOAD: Array<Field> = [
  ...ALL_COMMON_FIELDS_IN_PAYLOAD,
  Field.TEAM,
  Field.URGENCY,
  Field.ADDITIONAL_INFO,
];

export const PODCAST_ENTITY_TYPES = [EntityType.ENTITY_TYPE_EPISODE, EntityType.ENTITY_TYPE_SHOW];
